import _ from 'lodash';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Trans, useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { GridLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';
import { Scheme } from 'modules/overlay-canvas/types';
import history from 'modules/overlay-canvas/addons/history';
import { setHasChanges, setPreviewState } from 'modules/template-editor/store/editor/editor';
import { selectFabricManager, selectHasChange, selectIsInit, selectSavedScheme } from 'modules/template-editor/store/editor/selectors';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import useLocalStorage from 'modules/template-editor/ui/hooks/use-local-storage';
import useSaveScheme from 'modules/template-editor/ui/hooks/use-save-scheme';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import Canvas from './canvas';
import { Button, Container, Header, MainBlock, RightBlock, TemplateName } from './styles';
import { Zoom } from './zoom';
import { theme } from 'styles/theme/theme';

function Content() {
  const { t } = useLingui();
  const state = useLocation().state as null | { from: string };
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { activeTemplate } = useTemplates();
  const { activeBoard, activeScheme, isProcessed } = useBoards();
  const hasChange = useSelector(selectHasChange);
  const savedScheme = useSelector(selectSavedScheme);
  const isInit = useSelector(selectIsInit);
  const manager = useSelector(selectFabricManager);
  const { setData, getData } = useLocalStorage();
  const { saveScheme } = useSaveScheme();
  const boardName = useMemo(() => {
    return activeBoard?.name || getData()?.boardName;
  }, [activeBoard?.name, getData]);

  setData({
    scheme: activeScheme,
    boardId: activeBoard?.id,
    resolution: activeTemplate?.resolution,
    boardType: activeBoard?.type,
    from: state?.from,
    templateId: activeTemplate?.id,
    boardName: activeBoard?.name,
  });

  useEffect(() => {
    const historySubscriber = {
      update: (state: Scheme) => {
        if (!manager) return;
        if (!savedScheme) return;
        const isEqual = _.isEqual(state, savedScheme);
        dispatch(setHasChanges(!isEqual));
      },
    };

    history.onChangeState.subscribe(historySubscriber);

    return () => {
      history.onChangeState.unsubscribe(historySubscriber);
    };
  }, [dispatch, manager, savedScheme]);

  const onClickSave = useCallback(() => {
    saveScheme()
      .then(() => {
        toast.success(t`Табло успешно сохранено`);
      })
      .catch((e) => {
        console.error(e);
        toast.error(t`Не удалось сохранить`);
      });
  }, [saveScheme, t]);
  const onGoPreview = useCallback(() => {
    if (!manager) {
      console.warn({ event: 'go to preview', message: 'manager not found' });
      return;
    }

    if (!activeTemplate) {
      console.warn({ event: 'go to preview', message: 'template not found', activeTemplate });
      return;
    }

    if (!activeBoard) {
      console.warn({ event: 'go to preview', message: 'board not found', activeBoard });
      return;
    }

    history.disableHistory();
    manager.canvas.discardActiveObject();
    const historyState = history.getHistory();
    const curScheme = manager.getScheme(true);
    dispatch(
      setPreviewState({
        curHistoryIndex: historyState.index,
        history: historyState.history,
        scheme: curScheme,
        resolution: manager.resolution.value,
        backgroundColor: manager.canvas.backgroundColor?.toString(),
        viewport: manager.canvas.viewportTransform ? [...manager.canvas.viewportTransform] : undefined,
      }),
    );

    navigate(Pages.TEMPLATE.builders.preview(activeTemplate.id, activeBoard.id));
  }, [activeBoard, activeTemplate, dispatch, manager, navigate]);

  return (
    <MainBlock className={'flex-center h-full w-full flex-col'}>
      {!isInit && <GridLoader style={{ position: 'absolute' }} />}
      <div style={{ visibility: isInit ? 'visible' : 'hidden' }}>
        <Header>
          <div>
            <TemplateName>{boardName ?? t`Неизвестное табло`}</TemplateName>
          </div>
          <RightBlock>
            <Button onClick={onGoPreview} color={theme.colors.SurfaceWhite}>
              <Trans>Предпросмотр</Trans>
            </Button>
            <Button disabled={!hasChange} onClick={onClickSave} color={theme.colors.AccentNaplesYellow}>
              {isProcessed ? t`Сохранение` : hasChange ? t`Сохранить` : t`Сохранено`}
            </Button>
          </RightBlock>
        </Header>
        <Container>
          <Canvas />
          <Zoom />
        </Container>
      </div>
    </MainBlock>
  );
}

export default Content;
